import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Upload, X, ImageIcon, Loader2, Calendar as CalendarIcon, Download, Link as LinkIcon, Sparkles, UserCircle2 } from "lucide-react";
import CalendarManager from "@/components/CalendarManager";
import ReviewImportPanel from "@/components/ReviewImportPanel";
import AmenitiesPicker from "@/components/AmenitiesPicker";
import RulesEditor from "@/components/RulesEditor";
import HostProfilePanel from "@/components/HostProfilePanel";
import StripeConnectPanel from "@/components/StripeConnectPanel";
import FindConciergeriePanel from "@/components/FindConciergeriePanel";
import ConciergePaymentPanel from "@/components/ConciergePaymentPanel";

const EMPTY = {
  title: "", description: "", city: "", region: "Finistère",
  address: "", lat: 48.5, lng: -3.5,
  price_per_night: 100, max_guests: 2, bedrooms: 1, bathrooms: 1,
  property_type: "cottage", amenities: [], photos: [],
  cleaning_fee: 0, security_deposit: 0,
  weekly_discount_percent: 0, monthly_discount_percent: 0,
  capacity_adults: 2, capacity_children: 0, capacity_infants: 0,
  pets_allowed: false, has_sea_view: false,
  rules: {
    check_in_time: "16:00", check_out_time: "11:00",
    self_check_in: false, key_box: false,
    no_parties: true, no_smoking: true,
    pets_allowed: false, quiet_hours: true,
    max_visitors: 0, cleaning_conditions: "",
  },
  cancellation_policy: "moderate",
  concierge_partner_id: null,
  concierge_revenue_share_percent: 0,
  concierge_gets_cleaning_fee: false,
  beds: [],
  arrival_procedure: { text: "", photos: [] },
};

const BED_SIZES = [
  { v: "90x190", l: "90 × 190 cm (simple)" },
  { v: "140x190", l: "140 × 190 cm (double standard)" },
  { v: "140x200", l: "140 × 200 cm (double long)" },
  { v: "160x200", l: "160 × 200 cm (queen)" },
  { v: "200x200", l: "200 × 200 cm (king)" },
];

const REGIONS = ["Finistère", "Côtes-d'Armor", "Morbihan", "Ille-et-Vilaine"];
const TYPES = [
  { v: "cottage", l: "Longère / Penty" },
  { v: "seaside", l: "Bord de mer" },
  { v: "lighthouse", l: "Phare" },
  { v: "house", l: "Maison" },
  { v: "apartment", l: "Appartement" },
];

const CANCELLATION_POLICIES = [
  { v: "flexible", l: "Flexible", desc: "Annulation gratuite jusqu'à 24h avant l'arrivée" },
  { v: "moderate", l: "Modérée", desc: "Annulation gratuite jusqu'à 5 jours avant l'arrivée" },
  { v: "strict", l: "Stricte", desc: "Annulation gratuite uniquement dans les 48h après réservation" },
];

const resolvePhoto = (p) => p?.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`;

export default function OwnerDashboard() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "listings";
  const setTab = (k) => setParams({ tab: k });
  const [listings, setListings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [calendarFor, setCalendarFor] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [airbnbUrl, setAirbnbUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const fileInput = useRef(null);

  const load = async () => {
    if (!user) return;
    const [{ data: ls }, { data: bs }] = await Promise.all([
      api.get(`/listings`, { params: { owner_id: user.id } }),
      api.get(`/bookings/owner`),
    ]);
    setListings(ls);
    setBookings(bs);
  };

  useEffect(() => { load(); }, [user]);

  const startCreate = () => {
    setEditing("new");
    setForm(EMPTY);
  };

  const startEdit = (l) => {
    setEditing(l.id);
    setForm({ ...EMPTY, ...l });
  };

  const cancelEdit = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e?.preventDefault?.();
    try {
      if (editing === "new") {
        await api.post("/listings", form);
        toast.success("Annonce créée");
      } else {
        await api.put(`/listings/${editing}`, form);
        toast.success("Annonce mise à jour");
      }
      cancelEdit();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette annonce ?")) return;
    await api.delete(`/listings/${id}`);
    toast.success("Annonce supprimée");
    load();
  };

  const importFromAirbnb = async () => {
    if (!airbnbUrl.trim()) {
      toast.error("Collez un lien Airbnb");
      return;
    }
    setImporting(true);
    try {
      const { data } = await api.post("/import/airbnb", { url: airbnbUrl.trim() });
      const d = data.data || {};
      const filled = {};
      const counts = [];
      if (d.title)       { filled.title = d.title; counts.push("titre"); }
      if (d.description) { filled.description = d.description; counts.push("description"); }
      if (d.city)        { filled.city = d.city; counts.push("ville"); }
      if (d.region)      { filled.region = d.region; counts.push("région"); }
      if (d.address)     { filled.address = d.address; counts.push("adresse"); }
      if (typeof d.lat === "number") filled.lat = d.lat;
      if (typeof d.lng === "number") filled.lng = d.lng;
      if (d.max_guests)  { filled.max_guests = d.max_guests; counts.push("voyageurs"); }
      if (d.bedrooms)    { filled.bedrooms = d.bedrooms; counts.push("chambres"); }
      if (d.bathrooms)   { filled.bathrooms = d.bathrooms; counts.push("sdb"); }
      if (d.property_type) { filled.property_type = d.property_type; counts.push("type"); }
      if (d.photos?.length) { filled.photos = d.photos; counts.push(`${d.photos.length} photos`); }
      setForm((prev) => ({ ...prev, ...filled }));
      toast.success(`Import réussi : ${counts.join(" · ")}`);
      setAirbnbUrl("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'import");
    } finally {
      setImporting(false);
    }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.path);
      }
      setForm((p) => ({ ...p, photos: [...p.photos, ...uploaded] }));
      toast.success(`${uploaded.length} photo(s) ajoutée(s)`);
    } catch (e) {
      toast.error("Échec de l'upload");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const removePhoto = (idx) => {
    setForm((p) => ({ ...p, photos: p.photos.filter((_, i) => i !== idx) }));
  };

  // ====== Beds (couchage) helpers ======
  const addBed = () => {
    setForm((p) => ({ ...p, beds: [...(p.beds || []), { room: `Chambre ${(p.beds || []).length + 1}`, size: "140x190" }] }));
  };
  const updateBed = (i, patch) => {
    setForm((p) => ({ ...p, beds: (p.beds || []).map((b, idx) => idx === i ? { ...b, ...patch } : b) }));
  };
  const removeBed = (i) => {
    setForm((p) => ({ ...p, beds: (p.beds || []).filter((_, idx) => idx !== i) }));
  };

  // ====== Arrival procedure helpers ======
  const setArrivalText = (text) => {
    setForm((p) => ({ ...p, arrival_procedure: { ...(p.arrival_procedure || { photos: [] }), text } }));
  };
  const onArrivalPhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.path);
      }
      setForm((p) => ({
        ...p,
        arrival_procedure: {
          ...(p.arrival_procedure || { text: "" }),
          photos: [...((p.arrival_procedure || {}).photos || []), ...uploaded],
        },
      }));
      toast.success(`${uploaded.length} photo(s) ajoutée(s)`);
    } catch (err) {
      toast.error("Échec de l'upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  const removeArrivalPhoto = (idx) => {
    setForm((p) => ({
      ...p,
      arrival_procedure: {
        ...(p.arrival_procedure || { text: "" }),
        photos: ((p.arrival_procedure || {}).photos || []).filter((_, i) => i !== idx),
      },
    }));
  };

  const toggleAmenity = (a) => {
    setForm((p) => ({
      ...p,
      amenities: p.amenities.includes(a) ? p.amenities.filter((x) => x !== a) : [...p.amenities, a],
    }));
  };

  // Keep listing.has_sea_view in sync with amenity selection so the badge logic stays consistent
  const setAmenities = (next) => {
    setForm((p) => ({
      ...p,
      amenities: next,
      has_sea_view: next.includes("sea_view"),
    }));
  };

  const setRules = (next) => setForm((p) => ({ ...p, rules: next }));

  const togglePublish = async (l) => {
    try {
      if (l.published) {
        await api.post(`/listings/${l.id}/unpublish`);
        toast.success("Annonce mise en brouillon");
      } else {
        await api.post(`/listings/${l.id}/publish`);
        toast.success("Annonce publiée");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const totalEarnings = bookings.filter((b) => b.payment_status === "paid").reduce((s, b) => s + b.amount, 0);

  return (
    <div className="pt-24 sm:pt-28 min-h-screen flex">
      {/* Sidebar */}
      <aside className="hidden md:block w-64 bg-[#0A0A0A] text-white min-h-[calc(100vh-6rem)] shrink-0 sticky top-20 self-start">
        <div className="p-6 border-b border-white/10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/60">Espace</div>
          <div className="font-display text-xl font-bold mt-1">Propriétaire</div>
          <div className="text-xs text-white/60 mt-3 truncate">{user?.email}</div>
        </div>
        <nav className="p-4 flex flex-col gap-1">
          {[
            { k: "listings", l: "Mes annonces" },
            { k: "profile", l: "Profil hôte" },
            { k: "payments", l: "Paiements (Stripe)" },
            { k: "calendar", l: "Calendrier & tarifs" },
            { k: "bookings", l: "Réservations" },
            { k: "reviews", l: "Importer avis" },
            { k: "concierge", l: "Conciergerie & ménage" },
            { k: "earnings", l: "Revenus" },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`owner-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`text-left px-4 py-3 text-sm font-semibold transition-colors ${tab === t.k ? "bg-white text-[#0A0A0A]" : "hover:bg-white/10"}`}
            >
              {t.l}
            </button>
          ))}
        </nav>
      </aside>

      <main className="flex-1 px-4 sm:px-6 lg:px-10 py-10 bg-white">
        {tab === "listings" && (
          <>
            {!user?.stripe_charges_enabled && (
              <div className="mb-6 p-5 border-l-4 border-[#C9A14A] bg-[#FFFBEA]" data-testid="stripe-warn-banner">
                <div className="flex items-start gap-3">
                  <div className="text-xl">⚠️</div>
                  <div className="flex-1">
                    <div className="font-display text-lg font-bold">Connectez votre compte Stripe pour publier vos annonces</div>
                    <p className="text-sm text-neutral-700 mt-1">Vous pouvez créer et préparer vos annonces, mais elles resteront en brouillon (invisibles aux voyageurs) tant que votre compte de paiement n'est pas configuré.</p>
                    <button onClick={() => setTab("payments")} className="mt-3 text-sm font-bold underline underline-offset-4">Configurer Stripe →</button>
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between mb-8">
              <div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">Mes annonces</div>
                <h1 className="font-display text-4xl font-bold tracking-tight">{listings.length} annonce{listings.length > 1 ? "s" : ""}</h1>
              </div>
              <button data-testid="owner-new-listing" onClick={startCreate} className="bg-[#0A0A0A] text-white px-5 py-3 font-semibold flex items-center gap-2 hover:bg-[#1B2A49]">
                <Plus className="w-4 h-4" /> Nouvelle annonce
              </button>
            </div>

            {editing && (
              <form onSubmit={save} className="border-2 border-[#0A0A0A] p-6 mb-10 bg-[#FAFAFA]">
                <div className="flex items-center justify-between mb-6">
                  <div className="font-display text-2xl font-bold">{editing === "new" ? "Nouvelle annonce" : "Modifier l'annonce"}</div>
                  <button type="button" onClick={cancelEdit} className="text-sm underline">Annuler</button>
                </div>

                {editing === "new" && (
                  <div className="mb-8 p-5 border-2 border-dashed border-[#0A0A0A]/30 bg-white">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-10 h-10 bg-[#0A0A0A] text-white flex items-center justify-center shrink-0">
                        <Sparkles className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <div className="font-display text-lg font-bold">Importer depuis Airbnb</div>
                        <p className="text-sm text-neutral-600 mt-1">
                          Vous avez déjà une annonce sur Airbnb ? Collez son lien — on récupère titre, description, photos, ville, type… vous n'avez plus qu'à finaliser.
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="flex-1 flex items-center border border-black/15 bg-white px-3 py-2.5">
                        <LinkIcon className="w-4 h-4 text-neutral-400 shrink-0 mr-2" />
                        <input
                          data-testid="airbnb-import-url"
                          type="url"
                          value={airbnbUrl}
                          onChange={(e) => setAirbnbUrl(e.target.value)}
                          placeholder="https://www.airbnb.fr/rooms/12345…"
                          className="flex-1 bg-transparent outline-none text-sm"
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); importFromAirbnb(); } }}
                        />
                      </div>
                      <button
                        data-testid="airbnb-import-btn"
                        type="button"
                        onClick={importFromAirbnb}
                        disabled={importing || !airbnbUrl.trim()}
                        className="bg-[#0A0A0A] text-white px-5 py-2.5 font-semibold hover:bg-[#1B2A49] transition-colors flex items-center gap-2 disabled:opacity-50"
                      >
                        {importing ? <><Loader2 className="w-4 h-4 animate-spin" /> Import…</> : <><Download className="w-4 h-4" /> Importer</>}
                      </button>
                    </div>
                    <p className="text-[10px] text-neutral-500 mt-3 leading-relaxed">
                      💡 Seules les données <strong>publiques</strong> de l'annonce sont récupérées. Vous restez propriétaire et libre d'éditer le résultat avant publication.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Titre</div>
                    <input data-testid="f-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Ville</div>
                    <input data-testid="f-city" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Région</div>
                    <select data-testid="f-region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A] bg-white">
                      {REGIONS.map((r) => <option key={r}>{r}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Type</div>
                    <select data-testid="f-type" value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A] bg-white">
                      {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                    </select>
                  </label>
                  <label className="block md:col-span-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Adresse complète</div>
                    <input data-testid="f-address" value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="12 rue des Bés, 35400 Saint-Malo" className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
                    <p className="text-[10px] text-neutral-500 mt-1">L'adresse précise n'est divulguée qu'aux voyageurs après confirmation.</p>
                  </label>
                  <label className="block md:col-span-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Description</div>
                    <textarea data-testid="f-desc" required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Prix de base / nuit (€)</div>
                    <input data-testid="f-price" type="number" min={10} required value={form.price_per_night} onChange={(e) => setForm({ ...form, price_per_night: Number(e.target.value) })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
                    <p className="text-[10px] text-neutral-500 mt-1">Modulable jour par jour dans l'onglet Calendrier.</p>
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Voyageurs max</div>
                    <input data-testid="f-guests" type="number" min={1} required value={form.max_guests} onChange={(e) => setForm({ ...form, max_guests: Number(e.target.value) })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Adultes</div>
                    <input data-testid="f-adults" type="number" min={0} value={form.capacity_adults} onChange={(e) => setForm({ ...form, capacity_adults: Number(e.target.value) })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Enfants</div>
                    <input data-testid="f-children" type="number" min={0} value={form.capacity_children} onChange={(e) => setForm({ ...form, capacity_children: Number(e.target.value) })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Bébés</div>
                    <input data-testid="f-infants" type="number" min={0} value={form.capacity_infants} onChange={(e) => setForm({ ...form, capacity_infants: Number(e.target.value) })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Chambres</div>
                    <input data-testid="f-bed" type="number" min={0} required value={form.bedrooms} onChange={(e) => setForm({ ...form, bedrooms: Number(e.target.value) })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Salles de bain</div>
                    <input data-testid="f-bath" type="number" min={0} required value={form.bathrooms} onChange={(e) => setForm({ ...form, bathrooms: Number(e.target.value) })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Frais de ménage (€)</div>
                    <input data-testid="f-cleaning" type="number" min={0} value={form.cleaning_fee} onChange={(e) => setForm({ ...form, cleaning_fee: Number(e.target.value) })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
                    <p className="text-[10px] text-neutral-500 mt-1">Forfait facturé au voyageur. Non soumis à la commission de plateforme (10%).</p>
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Caution (€)</div>
                    <input data-testid="f-deposit" type="number" min={0} value={form.security_deposit} onChange={(e) => setForm({ ...form, security_deposit: Number(e.target.value) })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
                    <p className="text-[10px] text-neutral-500 mt-1">Montant non débité, à régler à l'arrivée si vous l'exigez.</p>
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Réduction semaine (%) — 7+ nuits</div>
                    <input data-testid="f-weekly-disc" type="number" min={0} max={50} value={form.weekly_discount_percent} onChange={(e) => setForm({ ...form, weekly_discount_percent: Number(e.target.value) })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Réduction mois (%) — 28+ nuits</div>
                    <input data-testid="f-monthly-disc" type="number" min={0} max={70} value={form.monthly_discount_percent} onChange={(e) => setForm({ ...form, monthly_discount_percent: Number(e.target.value) })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Latitude</div>
                    <input type="number" step="0.0001" value={form.lat} onChange={(e) => setForm({ ...form, lat: Number(e.target.value) })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Longitude</div>
                    <input type="number" step="0.0001" value={form.lng} onChange={(e) => setForm({ ...form, lng: Number(e.target.value) })} className="w-full border border-black/15 p-3 outline-none focus:border-[#0A0A0A]" />
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Animaux acceptés</div>
                    <div className="flex gap-2">
                      <button type="button" data-testid="f-pets-yes" onClick={() => setForm({ ...form, pets_allowed: true })} className={`flex-1 p-3 border font-semibold ${form.pets_allowed ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/15"}`}>Oui</button>
                      <button type="button" data-testid="f-pets-no" onClick={() => setForm({ ...form, pets_allowed: false })} className={`flex-1 p-3 border font-semibold ${!form.pets_allowed ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/15"}`}>Non</button>
                    </div>
                  </label>
                  <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-1">Vue mer</div>
                    <div className="flex gap-2">
                      <button type="button" data-testid="f-seaview-yes" onClick={() => setForm({ ...form, has_sea_view: true })} className={`flex-1 p-3 border font-semibold ${form.has_sea_view ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/15"}`}>Oui</button>
                      <button type="button" data-testid="f-seaview-no" onClick={() => setForm({ ...form, has_sea_view: false })} className={`flex-1 p-3 border font-semibold ${!form.has_sea_view ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/15"}`}>Non</button>
                    </div>
                  </label>
                </div>

                {/* Cancellation policy */}
                <div className="mt-6">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Politique d'annulation</div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {CANCELLATION_POLICIES.map((p) => (
                      <button
                        type="button"
                        key={p.v}
                        data-testid={`f-cancel-${p.v}`}
                        onClick={() => setForm({ ...form, cancellation_policy: p.v })}
                        className={`text-left p-4 border ${form.cancellation_policy === p.v ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-black/15"}`}
                      >
                        <div className="font-display text-lg font-bold">{p.l}</div>
                        <div className={`text-xs mt-1 ${form.cancellation_policy === p.v ? "text-white/70" : "text-neutral-500"}`}>{p.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-6">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Équipements</div>
                  <AmenitiesPicker value={form.amenities} onChange={setAmenities} />
                </div>

                <div className="mt-6">
                  <RulesEditor value={form.rules} onChange={setRules} />
                </div>

                {/* Couchage (tailles des lits) */}
                <div className="mt-6">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-3">Couchage & dimensions des lits</div>
                  <div className="space-y-2" data-testid="f-beds-list">
                    {(form.beds || []).map((b, i) => (
                      <div key={i} className="flex gap-2 items-center bg-white border border-black/10 p-3" data-testid={`f-bed-${i}`}>
                        <input
                          value={b.room || ""}
                          onChange={(e) => updateBed(i, { room: e.target.value })}
                          placeholder={`Chambre ${i + 1}`}
                          className="flex-1 border border-black/15 px-3 py-2 text-sm"
                          data-testid={`f-bed-room-${i}`}
                        />
                        <select
                          value={b.size || "140x190"}
                          onChange={(e) => updateBed(i, { size: e.target.value })}
                          className="border border-black/15 px-3 py-2 text-sm bg-white"
                          data-testid={`f-bed-size-${i}`}
                        >
                          {BED_SIZES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                        </select>
                        <button type="button" onClick={() => removeBed(i)} data-testid={`f-bed-remove-${i}`} className="p-2 border border-black/15 hover:border-[#991B1B] hover:text-[#991B1B]">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={addBed}
                    data-testid="f-bed-add"
                    className="mt-3 text-sm font-semibold border border-black/15 px-4 py-2 hover:bg-neutral-50 inline-flex items-center gap-2"
                  >
                    + Ajouter un lit
                  </button>
                  <p className="text-xs text-neutral-500 mt-2">Tailles standardisées : 90×190 (simple), 140×190, 140×200, 160×200 (queen), 200×200 (king).</p>
                </div>

                {/* Procédure d'arrivée */}
                <div className="mt-6 bg-[#F5F2EC] p-5 border border-black/10" data-testid="f-arrival-section">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#7A1535] mb-2">Procédure d'arrivée auto · 24h avant</div>
                  <p className="text-xs text-neutral-600 mb-3">Ce message sera envoyé <strong>automatiquement via la messagerie du site, 24 h avant le check-in</strong> du voyageur. Pensez à indiquer l'adresse précise, le code de boîte à clés, et les instructions d'accès.</p>
                  <textarea
                    rows={6}
                    value={(form.arrival_procedure && form.arrival_procedure.text) || ""}
                    onChange={(e) => setArrivalText(e.target.value)}
                    placeholder="Bonjour ! Voici les informations pour votre arrivée :
- Adresse : ...
- Code de la boîte à clés : ...
- Stationnement : ...
- Wifi : ...
- Mon numéro en cas d'imprévu : ..."
                    className="w-full border border-black/15 p-3 text-sm bg-white"
                    data-testid="f-arrival-text"
                  />
                  <div className="mt-4">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Photos d'aide (optionnel)</div>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                      {((form.arrival_procedure || {}).photos || []).map((p, i) => (
                        <div key={i} className="relative aspect-square bg-neutral-100 overflow-hidden border border-black/10" data-testid={`f-arrival-photo-${i}`}>
                          <img src={resolvePhoto(p)} alt="" className="w-full h-full object-cover" />
                          <button type="button" onClick={() => removeArrivalPhoto(i)} className="absolute top-1 right-1 p-1 bg-white">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      <label className="aspect-square border-2 border-dashed border-black/30 flex flex-col items-center justify-center text-xs font-semibold text-neutral-600 hover:border-[#0A0A0A] hover:text-[#0A0A0A] cursor-pointer" data-testid="f-arrival-upload">
                        <input type="file" accept="image/*" multiple onChange={onArrivalPhotoUpload} className="hidden" />
                        {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5 mb-1" />}
                        Ajouter
                      </label>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <ConciergePaymentPanel
                    listingCity={form.city}
                    listingRegion={form.region}
                    partnerId={form.concierge_partner_id}
                    sharePct={form.concierge_revenue_share_percent}
                    givesCleaning={form.concierge_gets_cleaning_fee}
                    cleaningFee={Number(form.cleaning_fee) || 0}
                    pricePerNight={Number(form.price_per_night) || 0}
                    onChange={(patch) => setForm((p) => ({ ...p, ...patch }))}
                  />
                </div>

                <div className="mt-6">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-500 mb-2">Photos</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {form.photos.map((p, i) => (
                      <div key={i} className="relative aspect-square bg-neutral-100 overflow-hidden border border-black/10">
                        <img src={resolvePhoto(p)} alt="" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => removePhoto(i)} className="absolute top-1 right-1 p-1 bg-white">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => fileInput.current?.click()}
                      data-testid="f-upload-btn"
                      className="aspect-square border-2 border-dashed border-black/30 flex flex-col items-center justify-center text-xs font-semibold text-neutral-600 hover:border-[#0A0A0A] hover:text-[#0A0A0A]"
                    >
                      {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5 mb-1" />}
                      Ajouter
                    </button>
                  </div>
                  <input ref={fileInput} data-testid="f-upload-input" type="file" accept="image/*" multiple onChange={onUpload} className="hidden" />
                </div>

                <div className="mt-8 flex gap-3">
                  <button data-testid="f-save" type="submit" className="bg-[#0A0A0A] text-white px-6 py-3 font-semibold hover:bg-[#1B2A49]">
                    {editing === "new" ? "Publier l'annonce" : "Enregistrer"}
                  </button>
                  <button type="button" onClick={cancelEdit} className="border-2 border-[#0A0A0A] px-6 py-3 font-semibold hover:bg-neutral-100">
                    Annuler
                  </button>
                </div>
              </form>
            )}

            <div className="space-y-3">
              {listings.length === 0 && !editing && (
                <div className="border-2 border-dashed border-black/15 p-12 text-center">
                  <ImageIcon className="w-10 h-10 mx-auto text-neutral-400 mb-3" />
                  <p className="text-neutral-600 mb-4">Aucune annonce. Commencez à louer en quelques minutes.</p>
                  <button onClick={startCreate} className="bg-[#0A0A0A] text-white px-5 py-3 font-semibold">Créer ma première annonce</button>
                </div>
              )}
              {listings.map((l) => (
                <div key={l.id} className="flex items-center gap-4 p-4 border border-black/10 bg-white hover:border-[#0A0A0A] transition-colors">
                  <div className="w-24 h-20 bg-neutral-100 overflow-hidden shrink-0 relative">
                    {l.photos?.[0] && <img src={resolvePhoto(l.photos[0])} alt="" className="w-full h-full object-cover" />}
                    {!l.published && (
                      <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-[#7A1535] text-white text-[8px] uppercase tracking-[0.18em] font-bold">Brouillon</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/listings/${l.id}`} className="font-semibold hover:underline truncate block">{l.title}</Link>
                    <div className="text-xs text-neutral-500 mt-1">{l.city} · {l.region} · {l.price_per_night}€/nuit</div>
                  </div>
                  <button
                    data-testid={`publish-listing-${l.id}`}
                    onClick={() => togglePublish(l)}
                    className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-bold border ${l.published ? "border-[#0A0A0A] text-[#0A0A0A]" : "bg-[#0A0A0A] text-white border-[#0A0A0A]"}`}
                    title={l.published ? "Mettre en brouillon" : "Publier l'annonce"}
                  >
                    {l.published ? "Dépublier" : "Publier"}
                  </button>
                  <button data-testid={`cal-listing-${l.id}`} onClick={() => { setTab("calendar"); setCalendarFor(l); }} className="p-2 border border-black/15 hover:border-[#0A0A0A]" title="Calendrier & prix"><CalendarIcon className="w-4 h-4" /></button>
                  <button data-testid={`edit-listing-${l.id}`} onClick={() => startEdit(l)} className="p-2 border border-black/15 hover:border-[#0A0A0A]"><Edit3 className="w-4 h-4" /></button>
                  <button data-testid={`del-listing-${l.id}`} onClick={() => remove(l.id)} className="p-2 border border-black/15 hover:border-[#991B1B] hover:text-[#991B1B]"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "profile" && (
          <HostProfilePanel />
        )}

        {tab === "concierge" && (
          <FindConciergeriePanel />
        )}

        {tab === "payments" && (
          <div className="max-w-4xl">
            <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">Paiements</div>
            <h1 className="font-display text-4xl font-bold tracking-tight mb-8">Recevez vos paiements</h1>
            <StripeConnectPanel />
            <div className="mt-8 p-5 border border-black/10 bg-[#FAFAFA] text-sm space-y-3">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold">Comment ça fonctionne</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-neutral-700">
                <div>
                  <strong className="block mb-1">1. Voyageur paie via Stripe</strong>
                  Carte bancaire, 3D Secure, paiement sécurisé.
                </div>
                <div>
                  <strong className="block mb-1">2. Répartition automatique</strong>
                  10% de frais de service vers MSB · le reste (nuitée + ménage + taxe de séjour) versé sur votre compte Stripe.
                </div>
                <div>
                  <strong className="block mb-1">3. Virement bancaire</strong>
                  Stripe vire automatiquement sur votre compte bancaire (délai par défaut : 2 jours après le check-in).
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "calendar" && (
          <>
            <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">Calendrier & tarifs</div>
                <h1 className="font-display text-4xl font-bold tracking-tight">
                  {calendarFor ? calendarFor.title : "Sélectionnez une annonce"}
                </h1>
                {calendarFor && (
                  <p className="text-sm text-neutral-600 mt-2">
                    Prix de base <strong>{calendarFor.price_per_night}€/nuit</strong> · cliquez sur les jours pour bloquer ou définir un tarif spécifique.
                  </p>
                )}
              </div>
              {listings.length > 0 && (
                <select
                  data-testid="calendar-listing-select"
                  value={calendarFor?.id || ""}
                  onChange={(e) => setCalendarFor(listings.find((l) => l.id === e.target.value) || null)}
                  className="border border-black/15 px-4 py-2.5 bg-white font-semibold"
                >
                  <option value="">— Choisir une annonce —</option>
                  {listings.map((l) => <option key={l.id} value={l.id}>{l.title}</option>)}
                </select>
              )}
            </div>
            {calendarFor ? (
              <CalendarManager listingId={calendarFor.id} basePrice={calendarFor.price_per_night} />
            ) : (
              <div className="border-2 border-dashed border-black/15 p-12 text-center">
                <CalendarIcon className="w-10 h-10 mx-auto text-neutral-400 mb-3" />
                <p className="text-neutral-600">Choisissez une annonce pour gérer son calendrier d'indisponibilités et ses tarifs jour par jour.</p>
              </div>
            )}
          </>
        )}

        {tab === "reviews" && (
          <ReviewImportPanel listings={listings} />
        )}

        {tab === "bookings" && (
          <>
            <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">Réservations reçues</div>
            <h1 className="font-display text-4xl font-bold tracking-tight mb-8">{bookings.length} réservation{bookings.length > 1 ? "s" : ""}</h1>
            <div className="space-y-3">
              {bookings.map((b) => (
                <div key={b.id} className="grid grid-cols-1 md:grid-cols-5 gap-3 p-4 border border-black/10 bg-white">
                  <div className="md:col-span-2">
                    <div className="font-semibold truncate">{b.listing_title}</div>
                    <div className="text-xs text-neutral-500 mt-1">Voyageur : {b.guest_name}</div>
                  </div>
                  <div className="text-sm">{b.check_in} → {b.check_out}</div>
                  <div className="text-sm">{b.guests} voyageur · {b.nights} nuits</div>
                  <div className="text-right">
                    <div className="font-bold">{b.amount}€</div>
                    <div className={`text-[10px] uppercase tracking-[0.2em] font-bold mt-1 ${b.payment_status === "paid" ? "text-[#166534]" : "text-neutral-500"}`}>
                      {b.payment_status === "paid" ? "Payée" : b.payment_status}
                    </div>
                  </div>
                </div>
              ))}
              {bookings.length === 0 && <p className="text-neutral-500 italic">Aucune réservation pour le moment.</p>}
            </div>
          </>
        )}

        {tab === "earnings" && (
          <>
            <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">Revenus</div>
            <h1 className="font-display text-4xl font-bold tracking-tight mb-8">{totalEarnings.toFixed(2)}€ encaissés</h1>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="border border-black/10 p-6 bg-white">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-500">Réservations payées</div>
                <div className="font-display text-3xl font-bold mt-2">{bookings.filter((b) => b.payment_status === "paid").length}</div>
              </div>
              <div className="border border-black/10 p-6 bg-white">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-500">En attente</div>
                <div className="font-display text-3xl font-bold mt-2">{bookings.filter((b) => b.payment_status !== "paid").length}</div>
              </div>
              <div className="border border-black/10 p-6 bg-white">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-500">Annonces actives</div>
                <div className="font-display text-3xl font-bold mt-2">{listings.length}</div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
